import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react'
import useSize from '@react-hook/size'

import type { validatorFunc } from '@/utils/validatorProvider'
import { compressImage } from '@/utils/compressImage'
import Validator from '@/pages/form/validator/Validator'
import iconDownload from '@assets/form/icon-download.png'

import { Document, Thumbnail } from 'react-pdf'

import './File.css'

interface FileProps {
	id: string
	placeholder: string
	label: string
	validator?: validatorFunc
	onChange?: (value: string) => void
	style?: React.CSSProperties
	value?: string
}

type FileType = 'image' | 'pdf' | 'other'

// Determine how to render a stored value. The value is either a base64 data
// URL (freshly uploaded or reloaded from the backend) or a plain path/URL.
function detectFileType(value: string, file: File | null): FileType {
	if (file) {
		if (file.type === 'application/pdf') return 'pdf'
		if (file.type.startsWith('image/')) return 'image'
	}
	if (value.startsWith('data:application/pdf')) return 'pdf'
	if (value.startsWith('data:image/')) return 'image'
	const lower = value.split('?')[0].toLowerCase()
	if (lower.endsWith('.pdf')) return 'pdf'
	if (/\.(png|jpe?g|gif|webp|bmp|svg|avif)$/.test(lower)) return 'image'
	return 'other'
}

// Data URLs carry no file name, so we only have one for fresh uploads or plain
// path values.
function displayName(value: string, file: File | null): string {
	if (file?.name) return file.name
	if (value === '' || value.startsWith('data:')) return ''
	return value.split('/').pop() ?? value
}

function File(props: FileProps) {
	const [dragActive, setDragActive] = useState(false)
	const [url, setUrl] = useState<string>('')
	const [file, setFile] = useState<File | null>(null)
	const [errors, setErrors] = useState<string[]>([])
	const ref = useRef<any>({ value: '' })
	const refFileContent = React.useRef<HTMLDivElement>(null!)
	const [width] = useSize(refFileContent)

	useEffect(() => {
		if (!props.value) return
		setUrl(props.value)
		ref.current.value = props.value
	}, [props.value])

	// Read the selected file as a base64 data URL so the preview survives a
	// reload: the data URL is persisted with the form and returned as the value.
	// Photos are compressed first to keep the submitted form small.
	const handleFile = async (data: File) => {
		const content = await compressImage(data)
		const reader = new FileReader()
		reader.onload = () => {
			const result = typeof reader.result === 'string' ? reader.result : ''
			setFile(data)
			setUrl(result)
			ref.current.value = result
			if (props.onChange) {
				props.onChange(result)
			}
		}
		reader.readAsDataURL(content)
	}

	const handleDrag = function (e: DragEvent<HTMLDivElement>) {
		e.preventDefault()
		e.stopPropagation()
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true)
		} else if (e.type === 'dragleave') {
			setDragActive(false)
		}
	}

	const handleDrop = function (e: DragEvent<HTMLDivElement>) {
		e.preventDefault()
		e.stopPropagation()
		setDragActive(false)
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFile(e.dataTransfer.files[0])
		}
	}

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		if (e.target.files && e.target.files[0]) {
			handleFile(e.target.files[0])
		}
	}

	const fileRender = () => {
		if (url === '') {
			return (
				<div className='fileRender'>
					<span className='filePlaceholder'>+</span>
				</div>
			)
		}

		const fileType = detectFileType(url, file)
		const name = displayName(url, file)

		return (
			<div className='fileRender'>
				{name !== '' && <p>{name}</p>}
				{fileType === 'pdf' && (
					<Document file={url} loading='' error='Aperçu indisponible'>
						<Thumbnail pageNumber={1} width={width} />
					</Document>
				)}
				{fileType === 'image' && <img src={url} alt={name || props.label} />}
				{fileType === 'other' && name === '' && <p>{url}</p>}
			</div>
		)
	}

	const handleError = (errors: string[]) => {
		setErrors(errors)
	}

	return (
		<Validator id={props.id} validator={props.validator} reference={ref} onError={handleError}>
			<div className='fileBox' onDragEnter={handleDrag} style={props.style}>
				<input
					className='input-file-upload'
					type='file'
					id={`${props.id}-input-file-upload`}
					multiple={false}
					onChange={handleChange}
				/>
				<label id='label-file-upload' htmlFor={`${props.id}-input-file-upload`}>
					<div className='fileHeader'>
						<h3>{props.label}</h3>
						<img src={iconDownload} alt='upload' />
					</div>
					<div className='fileContent' ref={refFileContent}>
						{fileRender()}
					</div>
				</label>
				{dragActive && (
					<div
						id='drag-file-element'
						onDragEnter={handleDrag}
						onDragLeave={handleDrag}
						onDragOver={handleDrag}
						onDrop={handleDrop}
					></div>
				)}
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error text-center'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}

export default File
